/* eslint-disable max-lines, max-lines-per-function */
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { Loader } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  artboardBackgroundAtom,
  mobileSizePlacementOpenAtom,
  useArtworkForm,
  useEnhancedImageSource,
  useImagesWarmFastCache
} from "@design-lab/entities/artwork";
import {
  activePinIdAtom,
  currentPinningSessionAtom,
  escapeHatchTriggerAtom,
  MAX_PINS,
  pendingInitialMessageAtom,
  pinBasedRedrawOverrideAtom,
  pinningModeAtom,
  pinningPanelExpandedAtom,
  pinningSessionsAtom,
  pinsAtom,
  showPinsAtom,
  useBulkDeletePins,
  useCancelPinningSession,
  useCompletePinningSession,
  useFetchPins,
  usePollSessionUpdated
} from "@design-lab/entities/pinning";
import { ConfirmActionModal } from "@design-lab/features/design-artwork/ui/ConfirmActionModal";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import {
  trackDesignLabPinningSessionCompleted,
  trackDesignLabPinningSessionCompletionFailed,
  trackPinSessionEscapeHatchFailed,
  trackPinsSessionCancellationAborted,
  trackPinsSessionCancelled,
  trackPinsSessionEditCancelFailed,
  trackPinsSessionEditCancelModalConfirmed,
  trackPinsSessionEditCancelModalStayed
} from "@design-lab/shared/lib/analytics";
import { useIsStaff } from "@design-lab/shared/lib/config";
import { useToast } from "@design-lab/shared/ui";
import { Button } from "@design-lab/shared/ui/design-system/Button";
import { Switch } from "@design-lab/shared/ui/design-system/Switch";
import { Tooltip, TooltipTrigger } from "@design-lab/shared/ui/design-system/Tooltip";
import { useArtboard } from "@design-lab/widgets/artboard/model/useArtboard";
import { ArtboardBase } from "../ArtboardBase";
import { RefineZoom } from "../refine-artwork/RefineZoom";
import { AlertModal } from "./AlertModal";
import { PinningActionBar } from "./PinningActionBar";
import { PinningFeedback } from "./PinningFeedback";
import { PinningHeader } from "./PinningHeader";
import { PinningPanel } from "./PinningPanel";
import { useEscapeHatch } from "./useEscapeHatch";

const PINNING_TOOLTIP_DISMISSED_KEY = "design-lab-pinning-tooltip-dismissed";

// PinningTooltip using the shared Tooltip component
function PinningTooltip({ message, onClose }: { message: string; onClose: () => void }) {
  const [isOpen, setIsOpen] = useState(true);

  if (!isOpen) return null;

  return (
    <TooltipTrigger isOpen={true}>
      <Button
        data-testid="pinning-tooltip-trigger"
        variant="link"
        className="pointer-events-none absolute left-1/2 top-[40%] z-10 h-1 min-h-0 w-1 min-w-0 -translate-x-1/2 -translate-y-1/2 cursor-default p-0 opacity-0"
        aria-hidden="true"
        excludeFromTabOrder
      />
      <Tooltip
        data-testid="pinning-artboard-tooltip"
        placement="top"
        className="pointer-events-auto max-w-[260px] border-blue-600 bg-blue-600 text-center text-white"
        showArrow={true}
        arrowClassName="fill-blue-600"
        onClose={() => {
          setIsOpen(false);
          onClose();
        }}
      >
        {message}
      </Tooltip>
    </TooltipTrigger>
  );
}

// eslint-disable-next-line max-lines-per-function
export function PinningArtboard() {
  const { isDesktop } = useResponsive();
  const { composition, placement } = useArtboard();
  useArtworkForm();
  const side = placement === "back" ? "back" : "front";
  const { source } = useEnhancedImageSource({ side });

  const [backgroundType, setBackgroundType] = useAtom(artboardBackgroundAtom);
  const [isMobileSizePlacementOpen, setIsMobileSizePlacementOpen] = useAtom(
    mobileSizePlacementOpenAtom
  );
  const artboardRef = useRef<HTMLDivElement>(null);

  const setPinningSessions = useSetAtom(pinningSessionsAtom);
  const [currentPinningSession, setCurrentPinningSession] = useAtom(currentPinningSessionAtom);
  const [pins, setPins] = useAtom(pinsAtom);
  const activePinId = useAtomValue(activePinIdAtom);
  const [showPins, setShowPins] = useAtom(showPinsAtom);
  const setPinningMode = useSetAtom(pinningModeAtom);
  const isPanelExpanded = useAtomValue(pinningPanelExpandedAtom);
  const [pendingInitialMessage, setPendingInitialMessage] = useAtom(pendingInitialMessageAtom);
  const [tooltipMode, setTooltipMode] = useState<"initial" | "add-next" | null>("initial");
  const [cancelModalType, setCancelModalType] = useState<"editSession" | "newSession" | null>(null);
  const [showUnfinishedPinModal, setShowUnfinishedPinModal] = useState(false);
  const [tooltipDismissed, setTooltipDismissed] = useState(
    () =>
      typeof window !== "undefined" &&
      localStorage.getItem(PINNING_TOOLTIP_DISMISSED_KEY) === "true"
  );
  const setPinBasedRedrawOverride = useSetAtom(pinBasedRedrawOverrideAtom);
  const escapeHatchTrigger = useAtomValue(escapeHatchTriggerAtom);
  const { mutate: completeSession, isPending: isCompleting } = useCompletePinningSession();
  const { mutate: cancelSession, isPending: isCancelling } = useCancelPinningSession();
  const isStaff = useIsStaff();
  const { error: showErrorToast } = useToast();
  const { mutate: bulkDeletePins, isPending: isBulkDeleting } = useBulkDeletePins();
  const { warmCache } = useImagesWarmFastCache();

  // Pre-warm mask cache when pins change
  useEffect(() => {
    const maskUrls = pins
      .map(pin => pin.segmentationMaskUrl)
      .filter((url): url is string => url != null);
    if (maskUrls.length > 0) {
      warmCache(maskUrls);
    }
  }, [pins, warmCache]);

  // Get current session info
  const currentArtworkId = currentPinningSession?.artworkId;
  const currentDesignProjectPlacementId = currentPinningSession?.designProjectPlacementId;
  const currentSessionId = currentPinningSession?.sessionId;

  // Check if there's at least one completed pin
  const hasCompletedPin = pins.some(pin => pin.state === "ready");
  const readyPinsCount = pins.filter(pin => pin.state === "ready").length;

  // Check if any pin is still processing or awaiting clarification.
  const hasAiAssistanceOngoing = pins.some(
    pin => pin.state === "processing" || pin.state === "needs_clarification"
  );

  // Compute new pins (pins created during this editing session)
  const newPins = useMemo(() => pins.filter(p => p.isNew), [pins]);
  const newPinIdsArray = useMemo(() => newPins.map(p => p.id), [newPins]);
  const hasNewPins = newPins.length > 0;

  // Determine if we're editing an existing session (has any pins that aren't new)
  const isEditingExistingSession = useMemo(() => pins.some(p => !p.isNew), [pins]);

  // Derive active pin and whether user can add new pins
  const activePin = pins.find(p => p.id === activePinId);
  const canAddNewPin =
    pins.length < MAX_PINS &&
    (!activePin || activePin.state === "ready" || activePin.state === "unsupported");

  const hasUnfinishedPin = pins.some(
    pin =>
      pin.state === "draft" || pin.state === "processing" || pin.state === "needs_clarification"
  );

  // Manage tooltip visibility based on pin states
  useEffect(() => {
    if (tooltipDismissed || pendingInitialMessage) {
      if (tooltipMode !== null) setTooltipMode(null);
      return;
    }

    // Hide initial tooltip when first pin is added
    if (pins.length > 0 && tooltipMode === "initial") {
      setTooltipMode(null);
      return;
    }

    // Hide tooltip when any unfinished pin exists
    if (hasUnfinishedPin) {
      setTooltipMode(null);
      return;
    }

    if (!hasCompletedPin || !canAddNewPin) {
      if (tooltipMode === "add-next") {
        setTooltipMode(null);
      }
      return;
    }

    // Show "add next pin" tooltip when a pin is completed and user can add more
    setTooltipMode("add-next");
  }, [
    pins,
    hasCompletedPin,
    canAddNewPin,
    tooltipMode,
    hasUnfinishedPin,
    tooltipDismissed,
    pendingInitialMessage
  ]);

  // Hydration: fetch existing pins on mount
  const { data: sessionData, isLoading: isLoadingPins } = useFetchPins(currentSessionId ?? null);
  const [currentUpdatedAt, setCurrentUpdatedAt] = useState<number | null>(null);

  // Poll for session updates
  const handleSessionUpdated = useCallback((newUpdatedAt: number) => {
    setCurrentUpdatedAt(newUpdatedAt);
    // The hook already invalidates queries, which will trigger a refetch
  }, []);

  usePollSessionUpdated(currentSessionId ?? null, currentUpdatedAt, handleSessionUpdated);

  useEffect(() => {
    if (!sessionData) return;

    // Handle response structure: { pins: [...], updatedAt?: number }
    const pinsData = (sessionData as { pins?: any[] })?.pins ?? [];

    // Update currentUpdatedAt if available
    if ((sessionData as { updatedAt?: number })?.updatedAt !== undefined) {
      setCurrentUpdatedAt((sessionData as { updatedAt: number }).updatedAt);
    }

    if (Array.isArray(pinsData) && pinsData.length > 0) {
      setPins(
        pinsData.map((p: any) => {
          const messages = Array.isArray(p.messages)
            ? p.messages
                .filter((m: any) => m?.role !== "ai_ack")
                .map((m: any) => ({
                  id: m.id || "",
                  role: m.role || "user",
                  content: m.content || "",
                  timestamp: m.timestamp || Date.now(),
                  ...(m.options ? { options: m.options } : {}),
                  ...(m.questionType ? { questionType: m.questionType } : {}),
                  ...(m.clarifications ? { clarifications: m.clarifications } : {}),
                  ...(m.textInfo ? { textInfo: m.textInfo } : {})
                }))
            : [];

          // If no messages but we have message field, create a synthetic AI message
          if (messages.length === 0 && p.message) {
            messages.push({
              id: `msg-${p.pinId}`,
              role: "ai",
              content: p.message,
              timestamp: Date.now()
            });
          }

          // Map API status to internal state
          const state = p.status || p.state || "draft";

          // Pins from API are existing (not new)
          return {
            id: p.pinId || p.id || "",
            coordinates: p.coordinates || { x: 0, y: 0 },
            state,
            instruction: p.instruction || p.summary,
            messages,
            areasOfInterest: p.areaOfInterest,
            segmentationMaskUrl: p.segmentationMaskUrl,
            isNew: false
          };
        })
      );
    }
  }, [sessionData, setPins]);

  // Exit pinning mode without cancelling the session (for editing existing sessions)
  const exitPinningMode = useCallback(() => {
    setPins([]);
    setCurrentPinningSession(null);
    setPendingInitialMessage(null);
    setPinningMode(false);
  }, [setPins, setCurrentPinningSession, setPendingInitialMessage, setPinningMode]);

  const handleEscapeHatchConfirmAction = useCallback(() => {
    if (!currentDesignProjectPlacementId || !currentSessionId) return;

    const cleanupPinningState = () => {
      setPinningSessions(prev => {
        const next = { ...prev };
        delete next[currentDesignProjectPlacementId];
        return next;
      });
      setPinBasedRedrawOverride(false);
      exitPinningMode();
    };

    cancelSession(
      { sessionId: currentSessionId },
      {
        onSuccess: () => {
          cleanupPinningState();
        },
        onError: (error: Error) => {
          trackPinSessionEscapeHatchFailed({
            triggerType: escapeHatchTrigger ?? "error",
            pinCount: pins.length,
            messageCount: activePin?.messages.length ?? 0,
            errorMessage: error?.message || "Unknown error"
          });
          showErrorToast({
            title: "Something went wrong",
            description:
              "We couldn't cancel the pin session, but you can still use the classic edit form."
          });
          cleanupPinningState();
        }
      }
    );
  }, [
    currentDesignProjectPlacementId,
    currentSessionId,
    cancelSession,
    setPinningSessions,
    setPinBasedRedrawOverride,
    exitPinningMode,
    pins,
    activePinId,
    escapeHatchTrigger,
    showErrorToast
  ]);

  const {
    showEscapeHatch,
    escapeHatchModalOpen,
    handleEscapeHatchClick,
    handleEscapeHatchConfirm,
    handleEscapeHatchDismiss
  } = useEscapeHatch({ onConfirm: handleEscapeHatchConfirmAction });

  const escapeHatchButton = showEscapeHatch ? (
    <button
      data-testid="escape-hatch-button"
      className="cursor-pointer text-xs font-normal text-gray-400 hover:text-gray-500 hover:underline"
      onClick={handleEscapeHatchClick}
      type="button"
    >
      Use classic edit form
    </button>
  ) : null;

  // Cancel and delete the entire session
  const cancelAndDeleteSession = useCallback(() => {
    if (!currentArtworkId || !currentDesignProjectPlacementId || !currentSessionId) return;

    trackPinsSessionCancelled({
      artworkId: currentArtworkId,
      sessionId: currentSessionId,
      pinsCount: pins.length,
      readyPinsCount,
      isStaff
    });
    cancelSession(
      { sessionId: currentSessionId, artworkId: currentArtworkId },
      {
        onSuccess: () => {
          setPinningSessions(prev => {
            const next = { ...prev };
            delete next[currentDesignProjectPlacementId];
            return next;
          });
          exitPinningMode();
        }
      }
    );
  }, [
    currentArtworkId,
    currentDesignProjectPlacementId,
    currentSessionId,
    pins.length,
    readyPinsCount,
    exitPinningMode,
    cancelSession,
    setPinningSessions,
    isStaff
  ]);

  // Handle cancel button click
  const handleCancelClick = useCallback(() => {
    if (!currentArtworkId || !currentSessionId) {
      exitPinningMode();
      return;
    }

    // If editing an existing session with new pins, show confirmation modal
    if (isEditingExistingSession && hasNewPins) {
      setCancelModalType("editSession");
      return;
    }

    // If editing an existing session with no new pins, just exit without API call
    if (isEditingExistingSession && !hasNewPins) {
      trackPinsSessionCancelled({
        artworkId: currentArtworkId,
        sessionId: currentSessionId,
        pinsCount: pins.length,
        readyPinsCount,
        isStaff
      });
      exitPinningMode();
      return;
    }

    // New session with pins: show confirmation warning
    if (pins.length > 0) {
      setCancelModalType("newSession");
      return;
    }

    // New session without pins: cancel immediately
    cancelAndDeleteSession();
  }, [
    currentArtworkId,
    currentSessionId,
    isEditingExistingSession,
    hasNewPins,
    pins.length,
    readyPinsCount,
    exitPinningMode,
    cancelAndDeleteSession,
    isStaff
  ]);

  // Handle confirmation modal confirm (remove new pins or cancel session)
  const handleConfirmCancel = useCallback(() => {
    if (!currentSessionId) return;

    // For editing existing session: bulk delete only new pins
    if (cancelModalType === "editSession") {
      trackPinsSessionEditCancelModalConfirmed({
        sessionId: currentSessionId,
        newPinsCount: newPinIdsArray.length
      });

      // Separate synced vs unsynced pins
      // Unsynced pins are in draft state with no messages - they only exist locally
      const unsyncedPinIds = newPins
        .filter(p => p.state === "draft" && p.messages.length === 0)
        .map(p => p.id);
      const syncedPinIds = newPinIdsArray.filter(id => !unsyncedPinIds.includes(id));

      // Always remove unsynced pins locally
      if (unsyncedPinIds.length > 0) {
        setPins(prev => prev.filter(p => !unsyncedPinIds.includes(p.id)));
      }

      // If all pins were unsynced, just exit without API call
      if (syncedPinIds.length === 0) {
        setCancelModalType(null);
        exitPinningMode();
        return;
      }

      // Only call API for synced pins
      bulkDeletePins(
        { sessionId: currentSessionId, pinIds: syncedPinIds },
        {
          onSuccess: () => {
            setCancelModalType(null);
            exitPinningMode();
          },
          onError: (error: Error) => {
            trackPinsSessionEditCancelFailed({
              sessionId: currentSessionId,
              newPinsCount: syncedPinIds.length,
              errorMessage: error?.message || "Unknown error"
            });
          }
        }
      );
      return;
    }

    // For new session: cancel entire session
    setCancelModalType(null);
    cancelAndDeleteSession();
  }, [
    currentSessionId,
    cancelModalType,
    newPinIdsArray,
    newPins,
    setPins,
    bulkDeletePins,
    exitPinningMode,
    cancelAndDeleteSession
  ]);

  // Handle confirmation modal stay (close modal, stay on page)
  const handleStayOnPage = useCallback(() => {
    if (currentSessionId && currentArtworkId) {
      if (cancelModalType === "editSession") {
        trackPinsSessionEditCancelModalStayed({
          sessionId: currentSessionId,
          newPinsCount: newPinIdsArray.length
        });
      } else if (cancelModalType === "newSession") {
        trackPinsSessionCancellationAborted({
          artworkId: currentArtworkId,
          sessionId: currentSessionId,
          pinsCount: pins.length
        });
      }
    }
    setCancelModalType(null);
  }, [currentSessionId, currentArtworkId, cancelModalType, newPinIdsArray.length, pins.length]);

  const surface = composition?.surfaces[placement];
  const frontSurface = composition?.surfaces?.front || composition?.surfaces?.frontLeftChest;
  const referenceBoundingBox =
    composition?.surfaces?.front?.boundingBox || composition?.surfaces?.frontLeftChest?.boundingBox;

  // Get all available surfaces for the current side
  const allSurfaces = [
    ...(composition?.surfaces?.front
      ? [{ surface: composition.surfaces.front, placement: "front" as const }]
      : []),
    ...(composition?.surfaces?.frontLeftChest
      ? [{ surface: composition.surfaces.frontLeftChest, placement: "frontLeftChest" as const }]
      : []),
    ...(composition?.surfaces?.back
      ? [{ surface: composition.surfaces.back, placement: "back" as const }]
      : [])
  ].filter(s => (placement === "back" ? s.placement === "back" : s.placement !== "back"));

  const handleImageClick = useCallback(() => {
    if (!tooltipMode || tooltipDismissed) return;

    if (tooltipMode === "add-next") {
      setTooltipDismissed(true);
      localStorage.setItem(PINNING_TOOLTIP_DISMISSED_KEY, "true");
    }
    setTooltipMode(null);
  }, [tooltipMode, tooltipDismissed]);

  const canvasProps = {
    surface: surface || null,
    referenceBoundingBox,
    placement,
    artworkImage: source.enhancedImage?.image,
    frontSurface,
    allSurfaces,
    disablePanning: false,
    onImageClick: handleImageClick
  };

  const controls = {
    showSideSwitcher: false,
    showZoom: true,
    showBackgroundToggle: true,
    showPickAModel: true,
    showToolbar: false,
    showProductBrowser: false,
    showRemoveBackground: false,
    showSizePlacement: false
  };

  const isNewSessionCancel = cancelModalType === "newSession";
  const cancelModalProps = {
    isOpen: cancelModalType !== null,
    onClose: handleStayOnPage,
    onConfirm: handleConfirmCancel,
    title: isNewSessionCancel ? "Are you sure?" : "Remove new pins?",
    message: isNewSessionCancel
      ? "If you exit, all the pins and changes will be deleted."
      : `This will remove ${newPinIdsArray.length} pin${newPinIdsArray.length === 1 ? "" : "s"}, are you sure?`,
    confirmText: isNewSessionCancel
      ? "Yes, Continue"
      : `Remove pin${newPinIdsArray.length === 1 ? "" : "s"}?`,
    cancelText: isNewSessionCancel ? "No" : "Stay on page",
    isLoading: isCancelling || isBulkDeleting,
    loadingText: isNewSessionCancel ? "Cancelling..." : "Removing pins..."
  };

  const handleDone = useCallback(() => {
    if (!currentSessionId || !currentArtworkId || !currentDesignProjectPlacementId) {
      setPinningMode(false);
      return;
    }

    if (hasAiAssistanceOngoing) {
      return;
    }

    completeSession(currentSessionId, {
      onSuccess: data => {
        trackDesignLabPinningSessionCompleted({
          artworkId: currentArtworkId,
          sessionId: currentSessionId,
          pinsCount: pins.length,
          readyPinsCount,
          summary: data.summary || "",
          isStaff
        });

        setPinningSessions(prev => ({
          ...prev,
          [currentDesignProjectPlacementId]: {
            sessionId: currentSessionId,
            state: "completed",
            summary: data.summary ?? null
          }
        }));
        setCurrentPinningSession(null);
        setPinningMode(false);
      },
      onError: (error: Error) => {
        trackDesignLabPinningSessionCompletionFailed({
          artworkId: currentArtworkId,
          sessionId: currentSessionId,
          errorMessage: error?.message || "Unknown error"
        });
        showErrorToast({
          title: "Something went wrong",
          description: "Please try again."
        });
      }
    });
  }, [
    currentSessionId,
    currentArtworkId,
    currentDesignProjectPlacementId,
    hasAiAssistanceOngoing,
    completeSession,
    pins.length,
    readyPinsCount,
    setPinningSessions,
    setCurrentPinningSession,
    setPinningMode,
    isStaff,
    showErrorToast
  ]);

  // Mobile: full-screen layout
  if (!isDesktop) {
    return (
      <div
        className="bg-background fixed inset-0 z-50 flex flex-col"
        data-testid="pinning-artboard"
      >
        {/* Header */}
        <PinningHeader />

        {/* Main content area - uses flex-col-reverse so panel expands upward */}
        <div className="flex min-h-0 flex-1 flex-col-reverse">
          {/* Pinning panel - at bottom, expands upward */}
          <div
            className={`mx-2 mb-1 overflow-hidden rounded-xl bg-white shadow-lg transition-[max-height,flex] duration-300 ease-out ${
              isPanelExpanded ? "mt-1 max-h-[100vh] min-h-0 flex-1" : "max-h-[40vh] shrink-0"
            }`}
          >
            <PinningPanel />
          </div>

          {/* Artboard area - above panel (due to reverse), shrinks when panel expands */}
          <div
            ref={artboardRef}
            className={`relative overflow-hidden transition-[max-height] duration-300 ease-out ${
              isPanelExpanded ? "max-h-0" : "max-h-[100vh] flex-1"
            }`}
            data-testid="pinning-artboard-container"
          >
            <ArtboardBase
              readonly={true}
              canvasProps={canvasProps}
              controls={{ ...controls, showZoom: false, showBackgroundToggle: false }}
              backgroundType={backgroundType}
              onBackgroundChange={setBackgroundType}
              currentSide={side}
              zoomConfig={{ initialZoom: 2, maxZoom: 10 }}
              loadingState={{ isLoading: false, imagePreview: undefined }}
              frontLoadingState={{ isLoading: false, imagePreview: undefined }}
              backLoadingState={{ isLoading: false, imagePreview: undefined }}
              mobileSizePlacementOpen={isMobileSizePlacementOpen}
              onMobileSizePlacementChange={setIsMobileSizePlacementOpen}
            />

            {tooltipMode && (
              <PinningTooltip
                message={
                  tooltipMode === "initial"
                    ? "Click anywhere on the image to drop a pin and add instructions for our artists."
                    : "Click anywhere on the image to add another pin"
                }
                onClose={() => {
                  setTooltipDismissed(true);
                  localStorage.setItem(PINNING_TOOLTIP_DISMISSED_KEY, "true");
                  setTooltipMode(null);
                }}
              />
            )}

            {/* Loading overlay while fetching pins */}
            {isLoadingPins && (
              <div
                className="absolute inset-0 z-20 flex items-center justify-center bg-white/70"
                data-testid="artboard-loading-overlay"
              >
                <div className="flex flex-col items-center gap-2">
                  <Loader className="h-8 w-8 animate-spin text-blue-700" />
                  <span className="text-sm font-medium text-gray-700">Loading pins...</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Toolbar buttons */}
        <div className="flex shrink-0 items-center justify-center gap-3 py-1">
          <div className="flex h-10 items-center gap-2 rounded-lg bg-white px-4 shadow-md">
            <Switch
              isSelected={showPins}
              onChange={setShowPins}
              data-hj-allow
              data-testid="show-pins-toggle"
            >
              <span className="text-sm font-medium text-gray-700">Show pins</span>
            </Switch>
          </div>
          <RefineZoom maxZoom={10} />
        </div>

        {/* Action bar at bottom */}
        <PinningFeedback />
        <PinningActionBar
          onCancel={handleCancelClick}
          onDone={handleDone}
          onDoneDisabledClick={() => hasUnfinishedPin && setShowUnfinishedPinModal(true)}
          isCancelDisabled={isCompleting || isCancelling || isBulkDeleting}
          isDoneDisabled={
            isCompleting || !hasCompletedPin || isCancelling || hasAiAssistanceOngoing
          }
          isLoading={isCompleting}
          variant="static"
          leftContent={escapeHatchButton}
        />

        <ConfirmActionModal {...cancelModalProps} />
        <ConfirmActionModal
          isOpen={escapeHatchModalOpen}
          onClose={handleEscapeHatchDismiss}
          onConfirm={handleEscapeHatchConfirm}
          title="Use classic edit form?"
          message="This will close the pin editor and let you describe your edits in a text form. Your placed pins will not be saved. Continue?"
          confirmText="Continue"
          cancelText="Stay here"
          isLoading={isCancelling}
          loadingText="Switching..."
        />
        <AlertModal
          isOpen={showUnfinishedPinModal}
          onClose={() => setShowUnfinishedPinModal(false)}
          title="Unfinished Pin"
          message="You have an unfinished pin."
        />
      </div>
    );
  }

  // Desktop layout
  return (
    <div className="relative h-full flex-1" data-testid="pinning-artboard">
      <div
        ref={artboardRef}
        className="h-full w-full overflow-hidden rounded-xl"
        data-testid="pinning-artboard-container"
      >
        <ArtboardBase
          readonly={true}
          canvasProps={canvasProps}
          controls={controls}
          backgroundType={backgroundType}
          onBackgroundChange={setBackgroundType}
          currentSide={side}
          zoomConfig={{ initialZoom: 2, maxZoom: 10 }}
          loadingState={{ isLoading: false, imagePreview: undefined }}
          frontLoadingState={{ isLoading: false, imagePreview: undefined }}
          backLoadingState={{ isLoading: false, imagePreview: undefined }}
          mobileSizePlacementOpen={isMobileSizePlacementOpen}
          onMobileSizePlacementChange={setIsMobileSizePlacementOpen}
        />
      </div>

      {tooltipMode && (
        <PinningTooltip
          message={
            tooltipMode === "initial"
              ? "Click anywhere on the image to drop a pin and add instructions for our artists."
              : "Click anywhere on the image to add another pin"
          }
          onClose={() => {
            setTooltipDismissed(true);
            localStorage.setItem(PINNING_TOOLTIP_DISMISSED_KEY, "true");
            setTooltipMode(null);
          }}
        />
      )}

      <div className="absolute right-[140px] top-2 z-10 flex h-9 items-center gap-2 rounded-md bg-white px-3 shadow-md">
        <Switch
          isSelected={showPins}
          onChange={setShowPins}
          data-hj-allow
          data-testid="show-pins-toggle"
        >
          <span className="text-sm font-medium text-gray-700">Show pins</span>
        </Switch>
      </div>

      {/* Loading overlay while fetching pins */}
      {isLoadingPins && (
        <div
          className="absolute inset-0 z-20 flex items-center justify-center bg-white/70"
          data-testid="artboard-loading-overlay"
        >
          <div className="flex flex-col items-center gap-2">
            <Loader className="h-8 w-8 animate-spin text-blue-700" />
            <span className="text-sm font-medium text-gray-700">Loading pins...</span>
          </div>
        </div>
      )}

      <PinningFeedback />

      <PinningActionBar
        onCancel={handleCancelClick}
        onDone={handleDone}
        onDoneDisabledClick={() => hasUnfinishedPin && setShowUnfinishedPinModal(true)}
        isCancelDisabled={isCompleting || isCancelling || isBulkDeleting}
        isDoneDisabled={isCompleting || !hasCompletedPin || isCancelling || hasAiAssistanceOngoing}
        isLoading={isCompleting}
        leftContent={escapeHatchButton}
      />

      <ConfirmActionModal {...cancelModalProps} />
      <ConfirmActionModal
        isOpen={escapeHatchModalOpen}
        onClose={handleEscapeHatchDismiss}
        onConfirm={handleEscapeHatchConfirm}
        title="Use classic edit form?"
        message="This will close the pin editor and let you describe your edits in a text form. Your placed pins will not be saved. Continue?"
        confirmText="Continue"
        cancelText="Stay here"
        isLoading={isCancelling}
        loadingText="Switching..."
      />
      <AlertModal
        isOpen={showUnfinishedPinModal}
        onClose={() => setShowUnfinishedPinModal(false)}
        title="Unfinished Pin"
        message="You have an unfinished pin."
      />
    </div>
  );
}
